import { authentication } from '@ecosystem/newron-client-lib';
import axios, { HttpStatusCode } from 'axios';
import { DateTime } from 'luxon';

import { getConfig } from '../config';
import {
  AccessLevel,
  Collection,
  CollectionDataType,
  CollectionInsight,
  CollectionListItem,
  CollectionTag,
  CollectionType,
  CreateInsight,
  CreateTemplateData,
  UpdatePermission,
  UserInfoPermission,
} from '../types';
import { getLongLivedToken } from '../utils';

const COLLECTIONS_ENDPOINT = 'v1/collections';
const ADD_INSIGHT_TO_COLLECTIONS_ENDPOINT = 'v1/add-insight-to-collections';
const DUPLICATE_COLLECTION_ENDPOINT = 'v1/duplicate-collection';
const COLLECTION_EXPORT_ENDPOINT = 'v1/export-request';
const COLLECTION_DOWNLOAD_ENDPOINT = 'v1/download';
const REORDER_INSIGHTS_ENDPOINT = 'v1/reorder-insights';
const COPY_INSIGHT_ENDPOINT = 'v1/copy-insight';
const CREATE_COLLECTION_ENDPOINT = 'v1/create-collection';
const INTERNAL_ENDPOINT = 'v1/internal';

export const TOKEN_EXPIRATION_THRESHOLD = 60 * 60 * 1000; // 1 hour

export type CreateInsightCollection<TInsightData> =
  | Pick<Collection<TInsightData>, 'name'>
  | (Pick<Collection<TInsightData>, 'id'> & { sortOrder?: number });

export type CreateInsights<TInsightData> = {
  insightPrototype: CreateInsight<TInsightData>;
  collections: CreateInsightCollection<TInsightData>[];
};

export interface CreatedInsight {
  id: string;
  name: string;
  createdAt: string;
  collection: {
    id: string;
    name: string;
    createdAt: string;
  };
}

export interface InsightResponseBody {
  insights: CreatedInsight[];
}
interface CollectionListResponseItem extends Omit<CollectionListItem, 'createdAt'> {
  createdAt: string;
}

export type CollectionListResponse = { items: CollectionListResponseItem[]; totalCount: number };

export type CollectionPermissionsResponse = {
  accessLevel: AccessLevel;
  userInfo?: Omit<UserInfoPermission, 'accessLevel'>;
  permanent?: boolean;
  userGroupInfo?: {
    userGroupId: number;
    clientId?: string;
    name?: string;
  };
};

export type FileReponse = { content: ArrayBuffer; filename: string };

export type ExportedCollection = FileReponse;

export type ExportedStatistics = FileReponse;

export type ClientImpersonation = { client_id: number; client_user_group_ids: string[] };

const getCollectionsUrl = () => `${getConfig().COLLECTIONS_API_PATH}/${COLLECTIONS_ENDPOINT}`;
const getCollectionsInternalUrl = () => `${getConfig().COLLECTIONS_API_PATH}/${INTERNAL_ENDPOINT}`;
const getCollectionsInternalDuplicationUrl = () => `${getCollectionsInternalUrl()}/duplicate-collection`;
const getCollectionInternalStatsUrl = () => `${getCollectionsInternalUrl()}/generate-stats`;
const getCollectionUrl = (collectionId: string) => `${getCollectionsUrl()}/${encodeURIComponent(collectionId)}`;
const getAddInsightToCollectionsUrl = () =>
  `${getConfig().COLLECTIONS_API_PATH}/${ADD_INSIGHT_TO_COLLECTIONS_ENDPOINT}`;
const getDuplicateCollectionUrl = () => `${getConfig().COLLECTIONS_API_PATH}/${DUPLICATE_COLLECTION_ENDPOINT}`;
const getInsightUrl = (collectionId: string, insightId: string) =>
  `${getCollectionUrl(collectionId)}/insights/${encodeURIComponent(insightId)}`;
const getCopyInsightUrl = () => `${getConfig().COLLECTIONS_API_PATH}/${COPY_INSIGHT_ENDPOINT}`;
const getExportCollectionUrl = () => `${getConfig().COLLECTIONS_API_PATH}/${COLLECTION_EXPORT_ENDPOINT}`;
const getDownloadCollectionUrl = (exportDownloadId: string) =>
  `${getConfig().COLLECTIONS_API_PATH}/${COLLECTION_DOWNLOAD_ENDPOINT}/${encodeURIComponent(exportDownloadId)}`;
const getUpdateInsightsOrderUrl = () => `${getConfig().COLLECTIONS_API_PATH}/${REORDER_INSIGHTS_ENDPOINT}`;
const getCollectionPermissionsUrl = (collectionId: string) => `${getCollectionUrl(collectionId)}/permissions`;
const getCollectionPermissionUrl = (collectionId: string, userId: string) =>
  `${getCollectionPermissionsUrl(collectionId)}/${encodeURIComponent(userId)}`;
const getCreateCollectionUrl = () => `${getConfig().COLLECTIONS_API_PATH}/${CREATE_COLLECTION_ENDPOINT}`;
const getStatisticsUrl = () => `${getCollectionsInternalUrl()}/stats`;

export async function addInsightToCollections<TInsightData>(
  insightPrototype: CreateInsight<TInsightData>,
  collections: CreateInsightCollection<TInsightData>[]
): Promise<CreatedInsight[]> {
  const token = await authentication.getAccessToken();

  const response = await axios.post<InsightResponseBody>(
    getAddInsightToCollectionsUrl(),
    { insightPrototype, collections },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return response.data.insights;
}

export async function editInsight(
  collectionId: string,
  insightId: string,
  updatedInsight: {
    name?: string;
    description?: string;
    data?: unknown;
    viewKey?: string;
    dataType?: CollectionDataType;
  }
): Promise<void> {
  const token = await authentication.getAccessToken();

  await axios.patch(
    getInsightUrl(collectionId, insightId),

    {
      name: updatedInsight.name,
      description: updatedInsight.description,
      data: updatedInsight.data,
      viewKey: updatedInsight.viewKey,
      dataType: updatedInsight.dataType,
    },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export async function copyInsight(
  collectionId: string,
  insightId: string,
  name: string,
  description?: string | null
): Promise<CollectionInsight<unknown>> {
  const token = await authentication.getAccessToken();

  const response = await axios.post(
    getCopyInsightUrl(),
    { insightId, collectionId, name, description },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return response.data;
}

export async function getCollections(): Promise<CollectionListResponse> {
  const token = await authentication.getAccessToken();

  const response = await axios.get<CollectionListResponse>(getCollectionsUrl(), {
    headers: { Authorization: `Bearer ${token}` },
  });

  return response.data;
}

export async function getCollection<TInsightData = unknown>(collectionId: string): Promise<Collection<TInsightData>> {
  const token = await authentication.getAccessToken();

  const response = await axios.get<Collection<TInsightData>>(getCollectionUrl(collectionId), {
    headers: { Authorization: `Bearer ${token}` },
  });

  return response.data;
}

export async function getInsight<TInsightData = unknown>(
  collectionId: string,
  insightId: string
): Promise<CollectionInsight<TInsightData>> {
  const token = await authentication.getAccessToken();

  const response = await axios.get<CollectionInsight<TInsightData>>(getInsightUrl(collectionId, insightId), {
    headers: { Authorization: `Bearer ${token}` },
  });

  return response.data;
}

export async function deleteInsight(collectionId: string, insightId: string): Promise<void> {
  const token = await authentication.getAccessToken();

  await axios.delete(getInsightUrl(collectionId, insightId), {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function editCollection(
  collectionId: string,
  payload:
    | { name: string; description: string; tags?: CollectionTag[] }
    | { lastViewedAt: DateTime }
    | { assignedClientIds: number[] }
    | {
        insights: Pick<CollectionInsight<unknown>, 'id' | 'data' | 'dataType' | 'viewKey' | 'name' | 'description'>[];
        insightsToDelete?: string[];
      }
): Promise<void> {
  const token = await authentication.getAccessToken();
  await axios.patch(getCollectionUrl(collectionId), payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function deleteCollection(collectionId: string): Promise<void> {
  const token = await authentication.getAccessToken();
  await axios.delete(`${getCollectionUrl(collectionId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function removePermissions(collectionId: string): Promise<void> {
  const token = await authentication.getAccessToken();
  await axios.delete(`${getCollectionPermissionUrl(collectionId, authentication.getUser().userId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function duplicateCollection({
  collectionId,
  newName,
  newDescription,
  updatedInsights,
  insightsToDelete,
}: {
  collectionId: string;
  newName: string;
  newDescription?: string;
  updatedInsights?: Pick<CollectionInsight<unknown>, 'id' | 'data' | 'dataType' | 'viewKey'>[];
  insightsToDelete?: string[];
}): Promise<CollectionListItem> {
  const token = await authentication.getAccessToken();
  const response = await axios.post(
    `${getDuplicateCollectionUrl()}`,
    {
      collectionId,
      name: newName,
      ...(newDescription && { description: newDescription }),
      ...(updatedInsights && { updatedInsights }),
      ...(insightsToDelete && { insightsToDelete }),
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const { isTemplate, ...collection } = response.data;

  const duplicatedCollection = {
    ...collection,
    type: isTemplate ? CollectionType.Template : CollectionType.Collection,
    createdAt: new Date(response.data.createdAt),
  };

  return duplicatedCollection;
}

type ExportResponse = 'created' | 'processing' | 'error';

export async function exportCollection(collectionId: string, collectionName: string): Promise<ExportResponse> {
  const token = await getLongLivedToken(TOKEN_EXPIRATION_THRESHOLD);
  const localStorgeImpersonation = localStorage.getItem('impersonation');
  const impersonation: ClientImpersonation = localStorgeImpersonation
    ? JSON.parse(localStorgeImpersonation)
    : undefined;
  const currentDate = DateTime.now();
  const userDateTimeIso8601 = currentDate.toISO();

  const response = await axios.post(
    `${getExportCollectionUrl()}`,
    { collectionId, collectionName, userDateTimeIso8601, impersonation },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response?.status === HttpStatusCode.Created) {
    return 'created';
  } else if (response?.status === HttpStatusCode.Accepted) {
    return 'processing';
  } else {
    return 'error';
  }
}

export async function getExportedCollection(exportDownloadId: string): Promise<ExportedCollection> {
  const token = await authentication.getAccessToken();

  const response = await axios.get(`${getDownloadCollectionUrl(exportDownloadId)}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    responseType: 'arraybuffer',
  });
  const disposition = response.headers['content-disposition'];
  const filename = disposition?.match(/filename="(.*)"/)?.[1];
  return {
    content: response.data,
    filename,
  };
}

export async function postReorderInsights(collectionId: string, insightIds: string[]): Promise<void> {
  const token = await authentication.getAccessToken();

  await axios.post(
    `${getUpdateInsightsOrderUrl()}`,
    { collectionId, insightIds },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export async function getCollectionPermissions(collectionId: string): Promise<CollectionPermissionsResponse[]> {
  const token = await authentication.getAccessToken();

  const response = await axios.get(`${getCollectionPermissionsUrl(collectionId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  return response.data;
}

export async function editCollectionPermissions(collectionId: string, permissions: UpdatePermission[]): Promise<void> {
  const token = await authentication.getAccessToken();

  await axios.patch(`${getCollectionPermissionsUrl(collectionId)}`, permissions, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function createCollection(collectionName: string): Promise<CollectionListItem> {
  const token = await authentication.getAccessToken();

  const response = await axios.post(
    getCreateCollectionUrl(),
    { name: collectionName },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  return response.data;
}

export async function createTemplate(templateData: CreateTemplateData): Promise<CollectionListItem> {
  const token = await authentication.getAccessToken();

  const response = await axios.post(
    getDuplicateCollectionUrl(),
    { ...templateData, isTemplate: true },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  return response.data;
}

export type StatisticsResponseItem = {
  createdAt: string;
  userType: string;
  active: boolean;
  insights: { proposition: string; count: number }[];
};

export type StatisticsResponse = StatisticsResponseItem[];

export async function getStatistics(): Promise<StatisticsResponse> {
  const token = await authentication.getAccessToken();

  const cachedResponse = sessionStorage.getItem('statisticsResponse');
  if (cachedResponse) {
    return JSON.parse(cachedResponse);
  }

  const response = await axios.get<StatisticsResponse>(getStatisticsUrl(), {
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = response.data;
  if (Array.isArray(data)) {
    data.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  sessionStorage.setItem('statisticsResponse', JSON.stringify(data));

  return data;
}

export async function duplicateCollectionInternal(
  collectionId: string,
  newName?: string,
  insightIds?: string[]
): Promise<CollectionListItem> {
  const token = await authentication.getAccessToken();
  const response = await axios.post(
    getCollectionsInternalDuplicationUrl(),
    { collectionId, ...(newName && { name: newName }), ...(insightIds?.length > 0 && { insightIds }) },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  return response.data;
}

export async function generateCollectionStats(): Promise<ExportedStatistics> {
  const token = await authentication.getAccessToken();

  const response = await axios.post(
    getCollectionInternalStatsUrl(),
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    }
  );

  const disposition = response.headers['content-disposition'];
  const filename = disposition?.match(/filename="(.*)"/)?.[1];
  return {
    content: response.data,
    filename,
  };
}
